import React, { useMemo } from "react";
import { Document, Page, Text, View, StyleSheet, PDFViewer } from "@react-pdf/renderer";

/**
 * Envelope face is landscape (~235×105mm).
 * PDF page is portrait (105×235mm) for portrait-fed printing.
 * A landscape sheet is rotated -90° onto that page so the address
 * still lands in the same place on the cover.
 *
 * Text is vector Helvetica (a built-in PDF font). A bitmap of the
 * letters was spreading ink and breaking the strokes when printed.
 */
const LANDSCAPE_W_MM = 235;
const LANDSCAPE_H_MM = 105;
const MM_TO_PT = 72 / 25.4;
const PORTRAIT_W = LANDSCAPE_H_MM * MM_TO_PT;
const PORTRAIT_H = LANDSCAPE_W_MM * MM_TO_PT;

const FONT_SIZE = 13;
const LINE_HEIGHT = 1.45;
const LABEL_COL = 58;
const SEP_COL = 12;

const styles = StyleSheet.create({
  page: {
    padding: 0,
    margin: 0,
  },
  sheet: {
    position: "absolute",
    width: PORTRAIT_H,
    height: PORTRAIT_W,
    left: (PORTRAIT_W - PORTRAIT_H) / 2,
    top: (PORTRAIT_H - PORTRAIT_W) / 2,
    transform: "rotate(-90deg)",
  },
  block: {
    position: "absolute",
    left: PORTRAIT_H * 0.25,
    top: PORTRAIT_W * 0.18,
    width: PORTRAIT_H * 0.5,
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  label: {
    width: LABEL_COL,
    fontFamily: "Helvetica",
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: "#000000",
    textAlign: "right",
  },
  sep: {
    width: SEP_COL,
    fontFamily: "Helvetica",
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: "#000000",
  },
  value: {
    width: PORTRAIT_H * 0.5 - LABEL_COL - SEP_COL,
    fontFamily: "Helvetica",
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: "#000000",
  },
});

function mergeAddressWithCity(address, city) {
  const addr = String(address || "").trim();
  const cty = String(city || "").trim();
  if (!cty) return addr;
  if (!addr) return cty;
  if (addr.toUpperCase().includes(cty.toUpperCase())) return addr;
  return `${addr}, ${cty}`;
}

function normalizeCoverEntry(entry = {}) {
  const customer = entry.customer || entry.customerDetails || {};
  const invoice = entry.invoice || entry;

  const name =
    invoice.billed_to_name ||
    invoice.billedTo?.name ||
    invoice.bill_to_name ||
    customer.name ||
    invoice.customer_name ||
    invoice.customerName ||
    entry.name ||
    "";
  const rawAddress =
    invoice.billed_to_address ||
    invoice.billedTo?.address ||
    customer.address ||
    invoice.customer_address ||
    invoice.customerAddress ||
    entry.address ||
    "";
  const area =
    invoice.billed_to_area ||
    invoice.billedTo?.area ||
    "";
  const address =
    rawAddress && area
      ? (rawAddress.toLowerCase().includes(area.toLowerCase())
          ? rawAddress
          : `${rawAddress}, ${area}`)
      : (rawAddress || area);
  const city =
    invoice.billed_to_city ||
    invoice.billedTo?.city ||
    customer.city ||
    invoice.customer_city ||
    invoice.customerCity ||
    entry.city ||
    "";
  const mobile =
    invoice.billed_to_mobile ||
    invoice.billedTo?.mobile ||
    customer.mobile ||
    invoice.customer_mobile ||
    invoice.customerMobile ||
    entry.mobile ||
    "";

  return {
    name: String(name || "")
      .trim()
      .toUpperCase(),
    address: mergeAddressWithCity(address, city).toUpperCase(),
    mobile: String(mobile || "")
      .trim()
      .toUpperCase(),
  };
}

function CoverLine({ label, value }) {
  const text = String(value || "").trim() || "-";
  return (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.sep}>:</Text>
      <Text style={styles.value}>{text}</Text>
    </View>
  );
}

function CoverPdfDocument({ covers }) {
  const pages = Array.isArray(covers) ? covers : [];
  return (
    <Document>
      {pages.map((item, index) => (
        <Page
          key={`cover-${index}`}
          size={[PORTRAIT_W, PORTRAIT_H]}
          style={styles.page}
        >
          <View style={styles.sheet}>
            <View style={styles.block}>
              <CoverLine label="Name" value={item.name} />
              <CoverLine label="Address" value={item.address} />
              <CoverLine label="Mobile" value={item.mobile} />
            </View>
          </View>
        </Page>
      ))}
    </Document>
  );
}

/**
 * Bill cover preview with PDFViewer (same pattern as invoice PDF modal).
 * @param {{ covers?: Array, coverData?: object }} props
 */
const InvoiceCoverPDF = ({ covers, coverData }) => {
  const list = useMemo(() => {
    const raw = Array.isArray(covers) ? covers : [coverData || {}];
    return raw.map(normalizeCoverEntry);
  }, [covers, coverData]);

  const listKey = useMemo(
    () => list.map((i) => `${i.name}|${i.address}|${i.mobile}`).join("||"),
    [list],
  );

  return (
    <div style={{ height: "100%" }}>
      <PDFViewer
        width="100%"
        height="100%"
        key={`cover-viewer-${listKey}`}
        showToolbar
      >
        <CoverPdfDocument covers={list} />
      </PDFViewer>
    </div>
  );
};

export default InvoiceCoverPDF;
export { normalizeCoverEntry, CoverPdfDocument };
